/**
 * Cashflow Forecast — derived from the operating forecast.
 *
 * RULE OF THUMB: only the opening cash balance (Jan 2026) is a hard input.
 * Everything else is computed:
 *   - Operating burn comes from the expense forecast (all parent depts summed)
 *   - Inflows: Series B, interest income, SR&ED refunds, grants — scheduled
 *   - Outflows other than burn: loan payment schedule
 *   - Closing cash compounds month over month
 *
 * If the user edits the forecast, recomputing this should re-derive every
 * month's closing cash balance. v1 uses cached demo numbers;
 * v2 will compute live from the editable forecast.
 *
 * All amounts in USD.
 */

import { forecast2026 } from './forecast2026';
import { CHILD_DEPTS, FY_LIST } from './forecast';
import { getMonthly } from './forecastOverrides';

// ─── The ONE hard input ──────────────────────────────────────────────────────
export const OPENING_CASH_2026 = 25882470; // Cash in bank, Jan 1 2026

// ─── Board-approved cash balance by quarter (FY2026 only) ────────────────────
// The board signs off on a quarterly cash status target; only FY2026 is approved.
// Labels MUST match computeQuarterEnds() output ("Q1 2026", etc.) so the chart
// can join the two series.
export const approvedCashByQuarter = [
  { label: 'Dec 2025', cash: 34961114 }, // opening anchor — same as forecast
  { label: 'Q1 2026', cash: 28663232 },
  { label: 'Q2 2026', cash: 19976090 },
  { label: 'Q3 2026', cash: 19169021 },
  { label: 'Q4 2026', cash: 11480271 },
];

// Reference values (demo) — kept for sanity-checking the computed cashflow.
// The chart no longer reads from this; it reads from computeQuarterEnds()
// so the table and chart can't drift.
const _referenceCashByQuarter = [ // eslint-disable-line no-unused-vars
  { label: 'Dec 2025', cash:  30289066 },
  { label: 'Mar 2026', cash:  25483825 },
  { label: 'Jun 2026', cash:  23106119 },
  { label: 'Sep 2026', cash:  21758793 },
  { label: 'Dec 2026', cash:  15376777 },
  { label: 'Mar 2027', cash:  13881228 },
  { label: 'Jun 2027', cash:   8029593 },
  { label: 'Sep 2027', cash: 91506591 }, // financing round closed (demo)
  { label: 'Dec 2027', cash: 115492331 },
  { label: 'Mar 2028', cash:  106722794 },
  { label: 'Jun 2028', cash:  84386999 },
  { label: 'Sep 2028', cash:  92160316 },
  { label: 'Dec 2028', cash:  87792550 },
  { label: 'Mar 2029', cash:  79600228 },
  { label: 'Jun 2029', cash:  63514594 },
  { label: 'Sep 2029', cash:  75497519 },
  { label: 'Dec 2029', cash:  52748136 },
  { label: 'Mar 2030', cash:  65705774 },
  { label: 'Jun 2030', cash:  57847641 },
  { label: 'Sep 2030', cash:  46860813 },
  { label: 'Dec 2030', cash:  34930997 },
  { label: 'Mar 2031', cash:  37583038 },
  { label: 'Jun 2031', cash:  23627894 },
  { label: 'Sep 2031', cash:  19246344 },
  { label: 'Dec 2031', cash:  16769393 },
];

export const FIRST_YEAR = 2026;
export const LAST_YEAR = 2031;
export const YEARS = [2026, 2027, 2028, 2029, 2030, 2031];
export const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// ─── Operating burn — sourced from the expense forecast ──────────────────────
// 2026 burn comes from forecast2026.js (sum of all child depts per month).
// 2027-2031 are cached "All Company" totals from the demo forecast — these
// will eventually come from forecast2027.js, forecast2028.js, etc., or a
// unified multi-year forecast structure.

// Burn for any year — sum overrides-aware monthly values across all child depts.
// Reads from the multi-year forecast (forecast.js) so all 2026-2031 are real data.
function getBurnFromForecast(year) {
  return Array.from({ length: 12 }, (_, m) => {
    return CHILD_DEPTS.reduce((sum, dept) => {
      return sum + (getMonthly(dept, year, m) || 0);
    }, 0);
  });
}

// All multi-year burn now derives from the multi-year forecast (per-dept × month).
// Hardcoded annual burn arrays no longer needed — consistency is automatic.

// Operating burn = direct sum of all child dept monthlies for the year.
// This is the SAME computation as the P&L Forecast's grand total. By construction,
// the cashflow's Operating burn line ≡ the P&L total for the same year. Math always maths.
export function getMonthlyBurn(year) {
  return getBurnFromForecast(year);
}

// Backward-compat (some consumers may still import this — they get a snapshot)
export const monthlyBurnByYear = {
  get 2026() { return getMonthlyBurn(2026); },
  get 2027() { return getMonthlyBurn(2027); },
  get 2028() { return getMonthlyBurn(2028); },
  get 2029() { return getMonthlyBurn(2029); },
  get 2030() { return getMonthlyBurn(2030); },
  get 2031() { return getMonthlyBurn(2031); },
};

// ─── Inflow schedule (demo) ──────────────────────────────────────────────────
// year → month index (0-11) → { interestIncome, seriesB, seriesC, srEdRefund, grant, other }
// Anything not listed is 0 for that month.
const yearMonthZero = () => Array.from({ length: 12 }, () => ({
  interestIncome: 0, seriesB: 0, seriesC: 0, srEdRefund: 0, grant: 0, other: 0,
}));

const inflows = {
  2026: yearMonthZero(),
  2027: yearMonthZero(),
  2028: yearMonthZero(),
  2029: yearMonthZero(),
  2030: yearMonthZero(),
  2031: yearMonthZero(),
};

// Interest income (GIC ~4%) — from PDF
const interest2026 = [32507, 702564, 0, 17799, 36733, 30499, 40459, 402521, 0, 0, 0, 101194];
const interest2027 = [61282, 51760, 37288, 43512, 32460, 32039, 27175, 33409, 29096, 290548, 389488, 340704];
const interest2028 = [361214, 268317, 383588, 262770, 342902, 310252, 316148, 287315, 284548, 319884, 337458, 331871];
const interest2029 = [243968, 277408, 243276, 255407, 301052, 247144, 271906, 272073, 257011, 187766, 202529, 227790];
const interest2030 = [226121, 221787, 146732, 165495, 191229, 149623, 184285, 130496, 173738, 164130, 122050, 140082];
const interest2031 = [143777, 103865, 124082, 109323, 114309, 110533, 79436, 95194, 78514, 78017, 67679, 63058];
[interest2026, interest2027, interest2028, interest2029, interest2030, interest2031].forEach((arr, yi) => {
  arr.forEach((v, mi) => { inflows[YEARS[yi]][mi].interestIncome = v; });
});

// Financing round closes Sept 2027 (month index 8) — demo
inflows[2027][8].seriesB = 96745580;

// Tax-credit refunds — annual, paid in March of following year typically (demo)
inflows[2027][2].srEdRefund = 995260;     // for 2026
inflows[2028][2].srEdRefund = 2929521;   // for 2027
inflows[2029][2].srEdRefund = 2881554;
inflows[2030][2].srEdRefund = 2729182;
inflows[2031][2].srEdRefund = 2131026;

// Grant 2026 — May through December
const grants2026 = [0, 0, 0, 0, 5736, 13353, 27680, 34675, 20443, 2382, 7514, 11945];
grants2026.forEach((v, i) => { inflows[2026][i].grant = v; });

// Other inflows — one-off in Jan 2026
inflows[2026][0].other = 581291;

// ─── Outflow schedule (other than operating burn) ────────────────────────────
// Loan payment schedule (demo), tapering off and ending in 2028
const loanPayment2026 = [3692, 4642, 4477, 3832, 4784, 4702, 3651, 4931, 4754, 4492, 3634, 4245];
const loanPayment2027 = [4649, 4051, 4997, 3608, 5037, 5032, 3784, 4293, 3459, 3872, 3833, 3058];
const loanPayment2028 = [4032, 4312, 3281, 3819, 3885, 3626, 3359, 4296, 3265, 0, 0, 0];
const loanPayment2029 = Array(12).fill(0);
const loanPayment2030 = Array(12).fill(0);
const loanPayment2031 = Array(12).fill(0);

const otherOutflows = {
  2026: { loanPayment: loanPayment2026 },
  2027: { loanPayment: loanPayment2027 },
  2028: { loanPayment: loanPayment2028 },
  2029: { loanPayment: loanPayment2029 },
  2030: { loanPayment: loanPayment2030 },
  2031: { loanPayment: loanPayment2031 },
};

// ─── Compute monthly cashflow ────────────────────────────────────────────────
/**
 * Returns an array of 72 monthly entries (Jan 2026 → Dec 2031), each with:
 * { year, monthIdx, monthLabel, opening, inflows: {...}, outflows: {...},
 *   totalInflows, totalOutflows, netChange, closing }
 */
export function computeCashflow() {
  const result = [];
  let opening = OPENING_CASH_2026;

  YEARS.forEach((year) => {
    const yearBurn = getMonthlyBurn(year); // compute once per year
    for (let m = 0; m < 12; m++) {
      const inflow = inflows[year][m];
      const burn = yearBurn[m] || 0;
      const loan = otherOutflows[year].loanPayment[m] || 0;

      const totalInflows =
        inflow.interestIncome + inflow.seriesB + inflow.seriesC +
        inflow.srEdRefund + inflow.grant + inflow.other;
      const totalOutflows = burn + loan;
      const netChange = totalInflows - totalOutflows;
      const closing = opening + netChange;

      result.push({
        year,
        monthIdx: m,
        monthLabel: `${MONTHS[m]} ${year}`,
        opening,
        inflows: { ...inflow },
        outflows: { burn, loanPayment: loan },
        totalInflows,
        totalOutflows,
        netChange,
        closing,
      });

      opening = closing; // next month opens with this month's closing
    }
  });

  return result;
}

/**
 * Walks the cashflow forecast forward starting from the given current cash
 * and month, EXCLUDING Series B / Series C raise inflows. Returns the first
 * month in which closing cash goes ≤ 0.
 *
 * Used by the Runway KPI: "what month do we run out if no new raise lands?"
 * A financing round is scheduled for Sep 2027 (demo); excluding it tells
 * the company how long it can survive on its own.
 *
 * Args:
 *   currentCashUsd   — opening cash today (e.g. live bank-feed total)
 *   fromYear         — 4-digit year (default real today's year)
 *   fromMonthIdx     — 0-11 (default real today's month)
 *
 * Returns:
 *   {
 *     cashOutLabel: 'Jul 2028' | null   // null = cash survives the whole forecast
 *     cashOutYear:  2028 | null
 *     cashOutMonthIdx: 6 | null
 *     monthsUntil:  27 | null            // count from start point to cash-out
 *     finalCash:    8_750_000 | -150_000 // closing cash at end of forecast window
 *   }
 */
export function computeRunwayMonth(currentCashUsd, fromYear, fromMonthIdx) {
  const today = new Date();
  // Default: start from the month AFTER today's. Today's month is partially
  // already burned and reflected in currentCashUsd, so walking it forward
  // would double-count. Caller can override by passing fromMonthIdx.
  let startYear = fromYear ?? today.getFullYear();
  let startMonth = fromMonthIdx ?? (today.getMonth() + 1);
  if (startMonth >= 12) { startMonth = 0; startYear += 1; }

  let cash = currentCashUsd;
  let monthsWalked = 0;

  for (const year of YEARS) {
    if (year < startYear) continue;
    const yearBurn = getMonthlyBurn(year);
    const startM = year === startYear ? startMonth : 0;
    for (let m = startM; m < 12; m++) {
      const inflow = inflows[year][m] || {};
      // EXCLUDE seriesB and seriesC — runway question is "what if no raise?"
      const totalInflows =
        (inflow.interestIncome || 0) +
        (inflow.srEdRefund || 0) +
        (inflow.grant || 0) +
        (inflow.other || 0);
      const burn = yearBurn[m] || 0;
      const loan = (otherOutflows[year]?.loanPayment?.[m]) || 0;
      const netChange = totalInflows - burn - loan;
      cash = cash + netChange;
      monthsWalked += 1;

      if (cash <= 0) {
        return {
          cashOutLabel: `${MONTHS[m]} ${year}`,
          cashOutYear: year,
          cashOutMonthIdx: m,
          monthsUntil: monthsWalked,
          finalCash: Math.round(cash),
        };
      }
    }
  }

  return {
    cashOutLabel: null,
    cashOutYear: null,
    cashOutMonthIdx: null,
    monthsUntil: null,
    finalCash: Math.round(cash),
  };
}

/**
 * Returns 24 quarter-end cash balances [{ label: "Q1 2026", year, quarter, cash }, ...]
 * Quarter end = March / June / September / December closing balances.
 */
export function computeQuarterEnds() {
  const cf = computeCashflow();
  const result = [];
  YEARS.forEach((year) => {
    [3, 6, 9, 12].forEach((endMonth, qi) => {
      const idx = (year - FIRST_YEAR) * 12 + (endMonth - 1);
      result.push({
        label: `Q${qi + 1} ${year}`,
        year,
        quarter: qi + 1,
        cash: cf[idx]?.closing || 0,
        netChange: cf[idx]?.closing - (qi === 0 ? OPENING_CASH_2026 : (cf[idx - 3]?.closing || 0)),
      });
    });
  });
  return result;
}

/**
 * Aggregate annual totals for the table summary (total inflows, total outflows, net change, closing cash).
 */
export function computeAnnual() {
  const cf = computeCashflow();
  return YEARS.map((year) => {
    const months = cf.filter((m) => m.year === year);
    return {
      year,
      opening: months[0].opening,
      totalInflows: months.reduce((s, m) => s + m.totalInflows, 0),
      totalOutflows: months.reduce((s, m) => s + m.totalOutflows, 0),
      netChange: months.reduce((s, m) => s + m.netChange, 0),
      closing: months[months.length - 1].closing,
      monthsArray: months,
    };
  });
}
